/**
 * @file GetQuote.jsx
 * @description Get Quote form page with Tailwind CSS
 */

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PriceList from '../components/PriceList';
import { api } from '../services/api';

const GetQuote = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    postcode: '',
    serviceType: '',
    urgency: 'Standard',
    description: ''
  });

  const [status, setStatus] = useState({ type: '', message: '' });
  const [showPricing, setShowPricing] = useState(false);
  const [errors, setErrors] = useState({});
  const [validatingPostcode, setValidatingPostcode] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const scrollToPricing = () => {
    // Show pricing and scroll to it
    setShowPricing(true);
    setTimeout(() => {
      const pricingSection = document.getElementById('pricing-section');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  };

  const serviceTypes = [
    'Plumbing Repair',
    'Gas Work',
    'Boiler Service',
    'Boiler Installation',
    'Central Heating',
    'Bathroom Installation',
    'Emergency Service',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleBlur = async (e) => {
    const { name, value } = e.target;
    const newErrors = { ...errors };

    // Live validation on blur
    switch (name) {
      case 'email':
        if (value && !validateEmail(value)) {
          newErrors.email = 'Please enter a valid email address';
        }
        break;
      case 'phone':
        if (value && !validatePhone(value)) {
          newErrors.phone = 'Please enter a valid UK phone number';
        }
        break;
      case 'postcode':
        if (value) {
          const isValid = await validatePostcode(value);
          if (!isValid) {
            newErrors.postcode = 'Please enter a valid UK postcode';
          }
        }
        break;
      default:
        break;
    }

    setErrors(newErrors);
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // UK phone number validation (accepts various formats)
    const phoneRegex = /^(\+44\s?7\d{3}|\(?07\d{3}\)?)\s?\d{3}\s?\d{3}$|^(\+44\s?[1-9]\d{1,4}|\(?0[1-9]\d{1,4}\)?)\s?\d{3,4}\s?\d{3,4}$/;
    const cleanPhone = phone.replace(/\s/g, '');
    return cleanPhone.length >= 10 && /^[\d\s\+\(\)]+$/.test(phone);
  };

  const validatePostcode = async (postcode) => {
    if (!postcode) return false;
    
    setValidatingPostcode(true);
    try {
      // Remove spaces and convert to uppercase for API call
      const cleanPostcode = postcode.replace(/\s/g, '').toUpperCase();
      const response = await fetch(`https://api.postcodes.io/postcodes/${cleanPostcode}/validate`);
      const data = await response.json();
      setValidatingPostcode(false);
      return data.result === true;
    } catch (error) {
      setValidatingPostcode(false);
      console.error('Postcode validation error:', error);
      return false;
    }
  };

  const validateForm = async () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!validatePhone(formData.phone)) {
      newErrors.phone = 'Please enter a valid UK phone number';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
    } else {
      const isValidPostcode = await validatePostcode(formData.postcode);
      if (!isValidPostcode) {
        newErrors.postcode = 'Please enter a valid UK postcode';
      }
    }

    if (!formData.serviceType) {
      newErrors.serviceType = 'Please select a service type';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Please describe the work you need';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ type: '', message: '' });

    const isValid = await validateForm();
    if (!isValid) {
      setStatus({
        type: 'error',
        message: 'Please correct the errors below and try again.'
      });
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
      return;
    }

    setStatus({ type: 'loading', message: 'Sending your quote request...' });

    try {
      await api.submitQuoteRequest(formData);
      setStatus({
        type: 'success',
        message: 'Thank you! We\'ve received your quote request and will contact you shortly.'
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        postcode: '',
        serviceType: '',
        urgency: 'Standard',
        description: ''
      });
      setErrors({});
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    } catch (error) {
      setStatus({
        type: 'error',
        message: error.message || 'Failed to submit request. Please try again or call us directly.'
      });
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 5000);
    }
  };

  return (
    <div className="get-quote-page">
      <Helmet>
        <title>Get a Free Quote | Metro Plumbing & Heating</title>
        <meta
          name="description"
          content="Request a free quote for plumbing, gas, or heating services in London. Quick response, competitive rates."
        />
      </Helmet>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 via-primary-700 to-primary-900 text-white py-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute w-96 h-96 bg-white rounded-full top-0 right-0 animate-pulse-slow"></div>
          <div className="absolute w-64 h-64 bg-white rounded-full bottom-0 left-0 animate-bounce-slow"></div>
        </div>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Get a Free Quote</h1>
            <p className="text-xl text-blue-100">
              Fill out the form below and we'll get back to you with a competitive quote
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form */}
            <div className="animate-fade-in-up">
              <div className="card p-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Request a Quote</h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      className={`input-field ${errors.name ? 'border-red-500 focus:border-red-500' : ''}`}
                      placeholder="John Smith"
                    />
                    {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`input-field ${errors.email ? 'border-red-500 focus:border-red-500' : ''}`}
                        placeholder="john@example.com"
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>
                    <div>
                      <label className="block text-gray-700 font-semibold mb-2">Phone *</label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`input-field ${errors.phone ? 'border-red-500 focus:border-red-500' : ''}`}
                        placeholder="07379 787766"
                      />
                      {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      className={`input-field ${errors.address ? 'border-red-500 focus:border-red-500' : ''}`}
                      placeholder="123 High Street"
                    />
                    {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Postcode *</label>
                    <div className="relative">
                      <input
                        type="text"
                        name="postcode"
                        value={formData.postcode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        className={`input-field ${errors.postcode ? 'border-red-500 focus:border-red-500' : ''}`}
                        placeholder="SW1A 1AA"
                      />
                      {validatingPostcode && (
                        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                        </div>
                      )}
                    </div>
                    {errors.postcode && <p className="text-red-500 text-sm mt-1">{errors.postcode}</p>}
                    <p className="text-gray-500 text-sm mt-1">We'll validate your postcode automatically</p>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Service Type *</label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleChange}
                      className={`input-field ${errors.serviceType ? 'border-red-500 focus:border-red-500' : ''}`}
                    >
                      <option value="">Select a service...</option>
                      {serviceTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                    {errors.serviceType && <p className="text-red-500 text-sm mt-1">{errors.serviceType}</p>}
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Urgency</label>
                    <div className="md:grid md:grid-cols-3 gap-4 flex flex-col">
                      {['Standard', 'Urgent', 'Emergency'].map((level) => (
                        <label
                          key={level}
                          className={`cursor-pointer p-4 rounded-lg border-2 text-center transition-all duration-300 ${
                            formData.urgency === level
                              ? 'border-primary-600 bg-primary-50'
                              : 'border-gray-200 hover:border-primary-300'
                          }`}
                        >
                          <input
                            type="radio"
                            name="urgency"
                            value={level}
                            checked={formData.urgency === level}
                            onChange={handleChange}
                            className="sr-only"
                          />
                          <span className="font-semibold capitalize">{level}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-700 font-semibold mb-2">Description *</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      className={`input-field resize-none ${errors.description ? 'border-red-500 focus:border-red-500' : ''}`}
                      placeholder="Please describe the work you need done..."
                    ></textarea>
                    {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={status.type === 'loading'}
                    className="btn-primary w-full text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status.type === 'loading' ? 'Sending...' : 'Get Free Quote'}
                  </button>
                </form>
              </div>
            </div>

            {/* Info Sidebar */}
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <div className="card p-8 mb-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Quick Response</h3>
                <div className="space-y-4">
                  <div className="flex items-start space-x-4 group">
                    <span className="flex-shrink-0 w-12 h-12 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xl font-bold relative group-hover:scale-110 transition-transform duration-300">
                      <span className="absolute inset-0 bg-primary-100 rounded-full animate-ping opacity-0 group-hover:opacity-75"></span>
                      <span className="relative">1</span>
                    </span>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Submit Form</h4>
                      <p className="text-gray-600">Fill out the quote request form with your details</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <span className="flex-shrink-0 w-12 h-12 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xl font-bold">2</span>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">We Review</h4>
                      <p className="text-gray-600">Our team reviews your request within 2 hours</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <span className="flex-shrink-0 w-12 h-12 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-xl font-bold">3</span>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">Get Quote</h4>
                      <p className="text-gray-600">Receive a detailed quote by phone or email</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card bg-gradient-to-br from-primary-600 to-primary-900 text-white p-8">
                <h3 className="text-2xl font-bold mb-4">Need Immediate Help?</h3>
                <p className="mb-6">For urgent issues or emergencies, call us directly</p>
                <a
                  href="tel:+447379787766"
                  className="block w-full px-6 py-4 bg-white text-primary-600 rounded-lg font-bold text-center hover:bg-gray-100 transform hover:-translate-y-1 transition-all duration-300 shadow-lg"
                >
                  📞 Call: 07379 787766
                </a>
              </div>

              <button
                onClick={scrollToPricing}
                className="mt-8 w-full px-6 py-4 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-900 transform hover:-translate-y-1 transition-all duration-300 shadow-lg"
              >
                💰 View Pricing Guide
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      {showPricing && (
        <section id="pricing-section" className="py-20 bg-white animate-fade-in-up">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <PriceList />
          </div>
        </section>
      )}

      {/* Success/Error Popup */}
      {showPopup && status.message && (
        <div className="fixed inset-0 flex items-center justify-center z-50 px-4">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 animate-fade-in"
            onClick={() => setShowPopup(false)}
          ></div>
          
          {/* Popup */}
          <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full animate-scale-in z-10">
            <button
              onClick={() => setShowPopup(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
            
            <div className="text-center">
              <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-3xl ${
                status.type === 'success' 
                  ? 'bg-green-100 text-green-600' 
                  : 'bg-red-100 text-red-600'
              }`}>
                {status.type === 'success' ? '✓' : '✗'}
              </div>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {status.type === 'success' ? 'Success!' : 'Oops!'}
              </h3>
              
              <p className="text-gray-600 mb-6">
                {status.message}
              </p>
              
              <button
                onClick={() => setShowPopup(false)}
                className={`px-8 py-3 rounded-lg font-semibold text-white transition-all duration-300 ${
                  status.type === 'success'
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GetQuote;
